import React, {useState} from 'react';
import {connect, DispatchProp} from "react-redux";
import {getAvailabilities, getCustomFields, getItems, getMenus, getPromotions} from "../../services/Menu/reducer";
import {useTranslation, withTranslation} from "react-i18next";
import {RootState} from "../../../reducers";
import {getStoreConfig} from "../../services/Store/reducer";
import {getSession, selectLocale} from "../../services/Session/reducer";
import {getCart} from "../../services/Cart/reducer";
import {ReactComponent as BasketFastSVG} from "../../../images/icons/basket-fast.svg";
import {ReactComponent as CalendarSVG} from "../../../images/icons/calendar.svg";
import {useSessionService} from "../../services/Session/sessionService";
import ChangePickDateDrawer from "../Drawer/ChangePickDateDrawer";
import {createKeyDownHandler} from "../../utils/keyDownHanlder";
import {DateHelper} from "../../services/Common/Date/DateHelper";
import {anyMenuHasPickDate} from "./utils";
import {getParsedSessionOrderType} from "../../utils/order";
import DisabledCartItemsModal from "./components/DisabledCartItemsModal/DisabledCartItemsModal";
import HoursHelper from "../../services/Menu/services/Availability/HoursHelper";
import DateService from "../../services/Common/Date/DateService";
import ItemHelper from "../../services/Menu/services/Item/ItemHelper";
import ItemViewHelper from "../../services/Menu/services/Item/ItemViewHelper";
import {OrderType} from "../../services/Api/types";
import AvailableHelper from "../../services/Menu/services/Availability/AvailableHelper";
import StoreUtils from "../../services/Store/StoreUtils";

interface OwnProps {
    errors: any[];
    onChange: (cart: any) => void;
    onChangeCustomField: (slug: any, value: any) => void;
    data: any;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;

const CartPickDateComponent = (props: Props) => {
    const cart = props.cart;
    const [showPickDateDrawer, setShowPickDateDrawer] = useState(false);
    const {t} = useTranslation();
    const sessionService = useSessionService();
    const [disabledCartItems, setDisabledCartItems] = useState<any[]>([]);
    const [lastTime, setLastTime] = useState<string | undefined>(props.session.pickup_at);
    const parsedOrderType = getParsedSessionOrderType(props.session.order_type);
    const orderTypeMenuDateAvailabilities = AvailableHelper.geDateAvailableAvailabilities(
        props.availabilities,
        props.menus.map((menu: any) => parsedOrderType && menu[parsedOrderType.toLowerCase()] && menu.availability_id),
        DateService.emptyOrNow(props.session.pickup_at)
    );
    const asSoonAsPossibleTime = AvailableHelper.getAddMinutesToPickDate(props.session.order_type, props.session.estimated_preparation_duration, props.session.estimated_delivery_duration);

    const hidePickDateModal = () => {
        setShowPickDateDrawer(false);
    }

    const setNow = async () => {
        let sessionData = {
            ...props.session,
            pickup_at: undefined
        }
        await sessionService.update(sessionData);
    }

    const handleAsSoonAsPossibleOptionChange = async () => {
        setLastTime(undefined);

        const possibleDisabledItems = getPossibleDisabledItemsForTime(undefined);

        if (possibleDisabledItems.length > 0) {
            setDisabledCartItems(possibleDisabledItems);
            return;
        }

        await setNow();
    }

    const handleOnLabelKeyDown = (option: string) => createKeyDownHandler<HTMLLabelElement>({
        keys: ["Enter", " "],
        preventDefault: true,
        action: async () => {
            if (option === "PICK_DATE") {
                setShowPickDateDrawer(true);
            } else {
                await handleAsSoonAsPossibleOptionChange();
            }
        }
    });

    const isPickDateOptionVisible = () => {
        const orderType = getParsedSessionOrderType(props.session.order_type);
        return StoreUtils.isPickDateEnabled(props.config) && anyMenuHasPickDate(props.menus, orderType);
    }

    const getPossibleDisabledItemsForTime = (date: string | undefined) => {
        const isOpen = HoursHelper.isOpen(props.config?.hours || [], DateService.emptyOrNow(date));

        if (!isOpen) {
            return cart.items.map((cartItem: any) => ItemHelper.getItem(props.items, cartItem.item_id)).filter(Boolean);
        }

        const cartItems = cart.items.map((cartItem: any) => {

            const item = ItemHelper.getItem(props.items, cartItem.item_id);

            if (!item) return undefined;

            return {
                item,
                itemView: ItemViewHelper.parseItem(item, props.availabilities, props.promotions, date, undefined, cart, props.session.order_type as OrderType, {
                    skipType: true
                })
            }
        }).filter(Boolean);

        return cartItems.filter(item => item?.itemView?.disabled || !HoursHelper.isOpen(orderTypeMenuDateAvailabilities.flatMap((availability: any) => availability.hours), DateService.emptyOrNow(date))).map(item => item?.item).filter(Boolean);
    }

    const handleOnHideDisabledCartItemsModal = () => {
        setDisabledCartItems([]);
    }

    const handleOnSubmitDisabledCartItemsModal = async () => {
        if (lastTime) {
            await handleSaveDate(lastTime, true);
        } else {
            await setNow();
        }
        handleOnHideDisabledCartItemsModal();
    }

    const handleSaveDate = async (date: string, skipCheckingItems?: true) => {
        setLastTime(date);

        if (!skipCheckingItems) {
            const possibleDisabledItems = getPossibleDisabledItemsForTime(date);
            if (possibleDisabledItems.length > 0) {
                setDisabledCartItems(possibleDisabledItems);
                return;
            }
        }

        await sessionService.update({
            ...props.session,
            pickup_at: date
        });
        setShowPickDateDrawer(false);
    }

    return (
        <React.Fragment>
            <div className="card-cart card-cart-with-columns">
                <div className={"container-fluid"}>
                    <h5 className={"card-cart-header"}>
                        {t("modules.cart.field.pickup_date.title")}
                    </h5>
                    <div className={"card-cart-body"}>
                        <div className="btn-groups-columns">
                            <div className="btn-group-toggle btn-checks" data-toggle="buttons">
                                <label tabIndex={0} onKeyDown={handleOnLabelKeyDown("NOW")}
                                       className={"btn btn-default btn-lg btn-check-full" + (!cart.pickup_at ? ' active' : '')}>
                                    <BasketFastSVG/>
                                    <span>
                                        <p className="mb-0">{t("common.word.as_soon_as_possible")}</p>
                                        {asSoonAsPossibleTime > 0 && <small
                                            className="text-primary">{DateHelper.formatDuration(asSoonAsPossibleTime)}</small>}
                                        </span>
                                    <span>

                                    </span>
                                    <input className="btn-check1" type="radio" name="pick_date" value="NOW"
                                           autoComplete="off" tabIndex={-1}
                                           checked={!props.session.pickup_at}
                                           onChange={() => handleAsSoonAsPossibleOptionChange()}/>
                                </label>
                                {isPickDateOptionVisible() && (
                                    <label tabIndex={0} onKeyDown={handleOnLabelKeyDown("PICK_DATE")}
                                           onClick={() => setShowPickDateDrawer(true)}
                                           className={"btn btn-default btn-lg btn-check-full" + (cart.pickup_at ? ' active' : '')}>
                                        <CalendarSVG/>
                                        <span>
                                            <p className="mb-0">{t("modules.cart.field.another_date.title")}</p>
                                            {props.session.pickup_at && <small
                                                className="text-primary">{DateHelper.getFormattedNearestDate(props.session.pickup_at, t, props.locale ?? "")}</small>}
                                        </span>
                                        <input className="btn-check1" type="radio" name="pick_date" value="PICK_DATE"
                                               autoComplete="off" tabIndex={-1}
                                               checked={!!props.session.pickup_at}/>
                                    </label>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ChangePickDateDrawer pickDate={props.session.pickup_at} showDrawer={showPickDateDrawer}
                                  orderType={props.session.order_type}
                                  withoutAsSoonAsPossibleOption
                                  handleSaveDate={handleSaveDate}
                                  onHide={hidePickDateModal} menuId={undefined}/>
            {disabledCartItems.length > 0 && <DisabledCartItemsModal show={disabledCartItems.length > 0}
                                                                     items={disabledCartItems}
                                                                     type="PICKUP_AT"
                                                                     pickupAt={lastTime}
                                                                     onSubmit={handleOnSubmitDisabledCartItemsModal}
                                                                     onHide={handleOnHideDisabledCartItemsModal}/>}
        </React.Fragment>
    )
}

const mapStateToProps = (state: RootState) => ({
    session: getSession(state.session),
    config: getStoreConfig(state.config),
    custom_fields: getCustomFields(state.menu),
    availabilities: getAvailabilities(state.menu),
    cart: getCart(state.cart),
    menus: getMenus(state.menu),
    locale: selectLocale(state.session),
    promotions: getPromotions(state.menu),
    items: getItems(state.menu),
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(CartPickDateComponent));
